import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Filter, ArrowUpNarrowWide, ArrowDownWideNarrow, Eye, Download } from "lucide-react";
import { listAllPayouts, getPayoutInvoice, uploadPayoutInvoice } from "../../../reducers/payouts.slice";
import { getInvoiceDetails } from "../../../reducers/shipment.slice";
import { convertDateFormatInUtc, formatNumberToDollar, downloadDocument, downloadImagesAsZip, countActiveFiltersAndSort } from "../../../utils/util";
import { generatePDF } from "../../../utils/generateInvoice";
import { AuthContext } from "../../../context/authContext";
import { Button } from "../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../components/ui/dialog";
import PageHeader from "../../../components/shared/PageHeader";
import StatusBadge from "../../../components/shared/StatusBadge";
import StatCard from "../../../components/shared/StatCard";
import SearchBar from "../../../components/shared/SearchBar";
import RefreshButton from "../../../components/shared/RefreshButton";
import DataTable from "../../../components/shared/DataTable";
import useMediaQuery from "../../../components/shared/useMediaQuery";

const STATUS_OPTIONS = [
  "PAYMENT_PENDING",
  "CREDIT_PENDING",
  "EARLY_PAYMENT",
  "EARLY_CREDIT",
  "PAYMENT_READY",
  "CREDIT_READY",
  "PAYMENT_DENIED",
  "CREDIT_DENIED",
  "PAYMENT_DONE",
  "CREDIT_DONE",
  "RECEIVING_DELAY",
  "CANCELLED",
];
const TYPE_OPTIONS = ["PayPal", "ACH"];

// Caps the sample used to sum lifetime paid + average payout speed for the
// strip (no aggregate endpoint exists) — the PENDING/READY counts are exact
// (fetched via total with an explicit status filter).
const PAYOUT_SAMPLE_LIMIT = 200;

function displayStatusLabel(status) {
  if (status === "CREDIT_DONE" || status === "PAYMENT_DONE") return "Credited";
  return undefined;
}

const MemberPayoutPage = () => {
  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const [filteredData, setFilteredData] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const stored = sessionStorage.getItem("payoutsMember_selectedStatuses");
    return stored ? JSON.parse(stored) : [];
  });
  const [selectedTypes, setSelectedTypes] = useState(() => {
    const stored = sessionStorage.getItem("payoutsMember_selectedTypes");
    return stored ? JSON.parse(stored) : [];
  });
  const [sortDirection, setSortDirection] = useState(() => sessionStorage.getItem("payoutsMember_sortDirection") || null);
  const [allLoadedData, setAllLoadedData] = useState([]);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loadingChunk, setLoadingChunk] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const stored = sessionStorage.getItem("payoutsMember_rowsPerPage");
    return stored ? JSON.parse(stored) : 5;
  });
  const [totalRows, setTotalRows] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isNavigatingToPage, setIsNavigatingToPage] = useState(false);
  const [pageCache, setPageCache] = useState(new Map());
  const [filterOpen, setFilterOpen] = useState(false);
  const [payoutStats, setPayoutStats] = useState(null);

  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewPayout, setViewPayout] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);

  const CHUNK_SIZE = 50;
  const isInitialRender = useRef(true);

  useEffect(() => {
    const now = new Date();

    Promise.all([
      dispatch(listAllPayouts({ skip: 0, limit: 1, payout_status: ["PAYMENT_PENDING", "CREDIT_PENDING"], type: [] })),
      dispatch(listAllPayouts({ skip: 0, limit: 1, payout_status: ["PAYMENT_READY", "CREDIT_READY"], type: [] })),
      dispatch(
        listAllPayouts({
          skip: 0,
          limit: PAYOUT_SAMPLE_LIMIT,
          payout_status: ["PAYMENT_DONE", "CREDIT_DONE"],
          type: [],
        })
      ),
    ]).then(([pendingRes, readyRes, doneRes]) => {
      const donePayouts = doneRes?.payload?.data || [];
      const lifetimePaid = donePayouts.reduce((sum, p) => sum + (p?.amount || 0), 0);
      const durations = donePayouts
        .filter((p) => p?.payment_date && p?.shipment_details?.shipment_date)
        .map((p) => (new Date(p.payment_date) - new Date(p.shipment_details.shipment_date)) / 86400000)
        .filter((days) => Number.isFinite(days) && days >= 0);
      const avgPayoutDays = durations.length
        ? Math.round((durations.reduce((sum, d) => sum + d, 0) / durations.length) * 10) / 10
        : null;

      setPayoutStats({
        pending: pendingRes?.payload?.total ?? 0,
        ready: readyRes?.payload?.total ?? 0,
        lifetimePaid,
        avgPayoutDays,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const loadDataChunk = async (chunkNumber = 0) => {
    const filterKey = JSON.stringify({
      selectedStatuses: [...selectedStatuses].sort(),
      selectedTypes: [...selectedTypes].sort(),
      search: searchTerm,
      sortDirection: sortDirection,
    });
    const cacheKey = `chunk-${chunkNumber}-${filterKey}`;

    if (pageCache.has(cacheKey)) {
      const cachedData = pageCache.get(cacheKey);
      if (cachedData.totalRows !== undefined) setTotalRows(cachedData.totalRows);
      return cachedData.data;
    }
    if (loadingChunk) return [];

    setLoadingChunk(true);
    try {
      const payload = { skip: chunkNumber * CHUNK_SIZE, limit: CHUNK_SIZE, payout_status: [], type: [] };
      if (selectedStatuses.length > 0) payload.payout_status = selectedStatuses;
      if (selectedTypes.length > 0) payload.type = selectedTypes;
      if (searchTerm?.trim()) payload.search = searchTerm.trim();
      if (sortDirection && sortDirection !== "null") {
        payload.sort_by = "AMOUNT";
        payload.sort_order = sortDirection;
      }

      const res = await dispatch(listAllPayouts(payload));
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setHasMoreData(false);
        setFilteredData([]);
        showToast(res?.error?.message, "error");
        return [];
      }
      if (!res?.payload?.data || !Array.isArray(res.payload.data)) {
        setHasMoreData(false);
        setFilteredData([]);
        return [];
      }
      if ((chunkNumber === 0 || !totalRows) && res?.payload?.total) {
        setTotalRows(res.payload.total);
      }

      const formattedPayouts = res?.payload?.data?.map((item) => {
        const shipmentDate = item?.shipment_details?.shipment_date
          ? convertDateFormatInUtc(item.shipment_details.shipment_date, "MMM D, YYYY")
          : "-";
        const member_full_name = `${item?.user_details?.first_name || ""} ${item?.user_details?.last_name || ""}`.trim();
        return {
          ...item,
          member_full_name,
          shipmentId: item?.shipment_details?.id,
          shipmentStatus: item?.shipment_details?.status,
          shipmentType: item?.shipment_details?.shipment_type,
          totalAmount: formatNumberToDollar(item?.amount),
          shipmentDate,
        };
      });

      setPageCache(
        (prev) => new Map(prev.set(cacheKey, { data: formattedPayouts, timestamp: Date.now(), totalRows: res?.payload?.total }))
      );

      const hasMore = formattedPayouts?.length === CHUNK_SIZE;
      setHasMoreData(hasMore);
      return formattedPayouts;
    } catch (error) {
      console.error("Error in loadDataChunk:", error);
      showToast("Error fetching data", "error");
      setHasMoreData(false);
      return [];
    } finally {
      setLoadingChunk(false);
    }
  };

  const openViewDialog = (row) => {
    setViewDialogOpen(true);
    setViewLoading(true);
    setViewPayout(row);
    setInvoiceDetails(null);

    dispatch(getInvoiceDetails({ id: row?.shipmentId })).then((response) => {
      if (response?.error && Object.keys(response?.error)?.length > 0) {
        setViewLoading(false);
        showToast(response?.error?.message, "error");
        return;
      }
      setInvoiceDetails(response?.payload);
      setViewLoading(false);
    });
  };

  const handleDownloadInvoice = () => {
    setDownloadingInvoice(true);
    const proceedToDownload = () => {
      dispatch(getPayoutInvoice({ payoutId: viewPayout?.id, document_type: "INVOICE" })).then((res) => {
        setDownloadingInvoice(false);
        if (res?.error && Object.keys(res?.error)?.length > 0) {
          showToast(res?.error?.message, "error");
          return;
        }
        if (!res?.payload?.length) {
          showToast("No files to download");
          return;
        }
        if (res.payload.length === 1) {
          const item = res.payload[0];
          downloadDocument(item?.document_bytes, `INVOICE ${viewPayout?.invoice_number || ""}`, item?.content_type);
        } else {
          downloadImagesAsZip(res.payload, "INVOICE");
        }
        showToast("File downloaded successfully", "success");
      });
    };

    if (viewPayout?.invoice_number) {
      proceedToDownload();
      return;
    }
    if (!invoiceDetails || Object.keys(invoiceDetails).length === 0) {
      setDownloadingInvoice(false);
      showToast("No invoice generated yet", "error");
      return;
    }
    const formdata = generatePDF(invoiceDetails);
    dispatch(
      uploadPayoutInvoice({
        formdata,
        uploadPayload: { id: viewPayout?.id, document_type: "INVOICE", invoice_number: invoiceDetails?.invoice_number },
      })
    ).then((uploadResponse) => {
      if (uploadResponse?.error && Object.keys(uploadResponse?.error)?.length > 0) {
        setDownloadingInvoice(false);
        showToast(uploadResponse?.error?.message, "error");
        return;
      }
      proceedToDownload();
    });
  };

  const columns = [
    { key: "id", label: "Payout ID", maxWidth: 110 },
    {
      key: "shipmentId",
      label: "Shipment",
      render: (row) => (
        <div>
          <div>{row?.shipmentId}</div>
          <div style={{ fontSize: 11, color: "#c3c8cc" }}>{row?.shipmentDate}</div>
        </div>
      ),
    },
    {
      key: "shipmentStatus",
      label: "Shipment Status",
      render: (row) => <StatusBadge status={row?.shipmentStatus} />,
    },
    { key: "totalAmount", label: "Amount" },
    {
      key: "status",
      label: "Status",
      render: (row) => <StatusBadge status={row?.status} label={displayStatusLabel(row?.status)} />,
    },
    { key: "invoice_number", label: "Invoice #", maxWidth: 110 },
    {
      key: "actions",
      label: "Action",
      align: "center",
      render: (row) => (
        <button
          type="button"
          aria-label="view payout"
          onClick={(e) => {
            e.stopPropagation();
            openViewDialog(row);
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            borderRadius: 8,
            background: "transparent",
            border: "1px solid rgba(255,255,255,0.1)",
            color: "#c3c8cc",
            cursor: "pointer",
          }}
        >
          <Eye size={14} />
        </button>
      ),
    },
  ];

  const handleStatusChange = (status, checked) => {
    setSelectedStatuses((prev) => (checked ? [...prev, status] : prev.filter((s) => s !== status)));
  };
  const handleTypeChange = (type, checked) => {
    setSelectedTypes((prev) => (checked ? [...prev, type] : prev.filter((s) => s !== type)));
  };

  const applyFilters = async () => {
    try {
      setLoadingChunk(true);
      setCurrentPage(0);
      setAllLoadedData([]);
      sessionStorage.setItem("payoutsMember_selectedStatuses", JSON.stringify(selectedStatuses));
      sessionStorage.setItem("payoutsMember_selectedTypes", JSON.stringify(selectedTypes));
      sessionStorage.setItem("payoutsMember_sortDirection", sortDirection);
      const results = await loadDataChunk(0);
      if (results?.length > 0) {
        setAllLoadedData(results);
      } else {
        setFilteredData([]);
        setAllLoadedData([]);
      }
    } catch (error) {
      console.error("Error applying filters:", error);
      showToast("Error applying filters", "error");
    } finally {
      setLoadingChunk(false);
    }
  };

  const handleFilterOpenChange = (nextOpen) => {
    setFilterOpen(nextOpen);
    if (!nextOpen) applyFilters();
  };

  const handleSortAscending = () => setSortDirection((prev) => prev === "ASC" ? null : "ASC");
  const handleSortDescending = () => setSortDirection((prev) => prev === "DESC" ? null : "DESC");

  const loadInitialData = async () => {
    const initialChunk = await loadDataChunk(0);
    setAllLoadedData(initialChunk);
  };

  const checkAndLoadMoreData = async (page, rowsPerPage) => {
    const requiredDataLength = (page + 1) * rowsPerPage;
    const currentDataLength = allLoadedData.length;
    if (currentDataLength >= requiredDataLength) return;
    if (!hasMoreData) return;
    if (loadingChunk) return;
    if (totalRows > 0 && currentDataLength >= totalRows) {
      setHasMoreData(false);
      return;
    }
    try {
      setIsNavigatingToPage(true);
      const nextChunk = Math.floor(currentDataLength / CHUNK_SIZE);
      const targetPage = page;
      const newData = await loadDataChunk(nextChunk);
      if (newData && newData.length > 0) {
        setAllLoadedData((prev) => [...prev, ...newData]);
        setTimeout(() => {
          const startIndex = page * rowsPerPage;
          const endIndex = startIndex + rowsPerPage;
          const combinedData = [...allLoadedData, ...newData];
          setCurrentPage(targetPage);
          setFilteredData(combinedData.slice(startIndex, endIndex));
        }, 100);
      } else {
        setHasMoreData(false);
      }
    } catch (error) {
      console.error("Error loading more data:", error);
      showToast("Error loading data", "error");
    } finally {
      setIsNavigatingToPage(false);
    }
  };

  const handleChangePage = async (newPage) => {
    if (isNavigatingToPage || loadingChunk) return;
    const requiredDataLength = (newPage + 1) * rowsPerPage;
    try {
      setIsNavigatingToPage(true);
      if (allLoadedData.length < requiredDataLength && hasMoreData) {
        await checkAndLoadMoreData(newPage, rowsPerPage);
      } else {
        const startIndex = newPage * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        setCurrentPage(newPage);
        setFilteredData(allLoadedData.slice(startIndex, endIndex));
      }
    } catch (error) {
      console.error("Error changing page:", error);
      showToast("Error loading page data", "error");
    } finally {
      setIsNavigatingToPage(false);
    }
  };

  useEffect(() => {
    if (isNavigatingToPage || loadingChunk) return;
    if (!allLoadedData?.length) return;
    const startIndex = currentPage * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const pageData = allLoadedData.slice(startIndex, endIndex);
    if (pageData.length > 0) {
      setFilteredData(pageData);
    } else if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [rowsPerPage, allLoadedData]);

  const handleChangeRowsPerPage = async (newValue) => {
    setRowsPerPage(newValue);
    sessionStorage.setItem("payoutsMember_rowsPerPage", JSON.stringify(newValue));
    setCurrentPage(0);
    if (allLoadedData.length < newValue && hasMoreData && !loadingChunk) {
      checkAndLoadMoreData(0, newValue);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    if (isSearching) return;
    const performSearch = async () => {
      try {
        setIsSearching(true);
        setCurrentPage(0);
        setAllLoadedData([]);
        const results = await loadDataChunk(0);
        if (results?.length > 0) {
          setAllLoadedData(results);
        } else {
          setFilteredData([]);
          setAllLoadedData([]);
        }
      } finally {
        setIsSearching(false);
      }
    };
    performSearch();
  }, [debouncedSearch]);

  const handleRefresh = () => {
    const currentPageBeforeRefresh = currentPage;
    setPageCache(new Map());
    setAllLoadedData([]);
    setHasMoreData(true);
    loadInitialData().then(() => {
      if (currentPageBeforeRefresh > 0) {
        setTimeout(() => setCurrentPage(currentPageBeforeRefresh), 100);
      }
    });
  };

  const activeFilterCount = countActiveFiltersAndSort(sortDirection, selectedStatuses, selectedTypes);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent style={{ maxWidth: 640, maxHeight: "92vh" }}>
          <DialogHeader>
            <DialogTitle>Payout {viewPayout?.id}</DialogTitle>
            <DialogDescription>Payout summary, shipment receiving, and invoice.</DialogDescription>
          </DialogHeader>

          {viewLoading ? (
            <div style={{ padding: "40px 0", textAlign: "center", color: "#c3c8cc", fontSize: 13 }}>Loading…</div>
          ) : (
            <div className="member-scroll" style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: 10 }}>
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
                <StatusBadge status={viewPayout?.status} label={displayStatusLabel(viewPayout?.status)} />
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "18px 20px",
                  marginBottom: 20,
                  padding: 18,
                  borderRadius: 10,
                  background: "rgba(255,255,255,0.02)",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                {[
                  ["PAYOUT ID", viewPayout?.id],
                  ["MEMBER NAME", viewPayout?.member_full_name],
                  ["SHIPMENT ID", viewPayout?.shipmentId],
                  ["SHIPMENT DATE", viewPayout?.shipmentDate],
                  ["SHIPMENT STATUS", viewPayout?.shipmentStatus?.replace(/_/g, " ")],
                  ["SHIPMENT TYPE", viewPayout?.shipmentType?.replace(/_/g, " ")],
                  ["AMOUNT", viewPayout?.totalAmount],
                  ["NOTES", viewPayout?.notes],
                ]
                  .filter(([, value]) => value)
                  .map(([label, value]) => (
                    <div key={label}>
                      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.05em", color: "#c3c8cc", marginBottom: 5 }}>
                        {label}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: "#c3c8cc", whiteSpace: "pre-line" }}>{value}</div>
                    </div>
                  ))}
              </div>

              {invoiceDetails?.shipment_boxes?.length > 0 && (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: "#fff" }}>
                      Invoice Summary
                      {viewPayout?.invoice_number && (
                        <span style={{ fontSize: 11.5, color: "#c3c8cc", fontWeight: 500, marginLeft: 8 }}>
                          #{viewPayout.invoice_number}
                        </span>
                      )}
                    </div>
                    <Button variant="outline" size="sm" onClick={handleDownloadInvoice} disabled={downloadingInvoice}>
                      <Download size={12} style={{ marginRight: 6 }} />
                      {downloadingInvoice ? "Downloading…" : "Download"}
                    </Button>
                  </div>
                  {invoiceDetails.shipment_boxes.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        padding: "9px 0",
                        borderTop: index === 0 ? "none" : "1px solid rgba(255,255,255,0.05)",
                        fontSize: 12.5,
                      }}
                    >
                      <span style={{ color: "#c3c8cc" }}>
                        {item?.name} <span style={{ color: "#c3c8cc" }}>×{item?.quantity}</span>
                      </span>
                      <span style={{ color: "#fff", fontFamily: "ui-monospace, monospace" }}>
                        {formatNumberToDollar((item?.cashout_price || 0) * (item?.quantity || 0))}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div style={{ marginBottom: 20 }}>
        <PageHeader title="Shipment Payouts" subtitle="Your shipment payouts to your wallet can be tracked here." />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? "145px" : "180px"}, 1fr))`,
          gap: isMobile ? 12 : 16,
          marginBottom: 20,
        }}
      >
        <StatCard label="PENDING" value={payoutStats ? payoutStats.pending : "—"} accent="#e8b84b" detail="awaiting processing" />
        <StatCard label="READY" value={payoutStats ? payoutStats.ready : "—"} accent="#22d3ee" detail="ready to credit" />
        <StatCard
          label="LIFETIME PAID"
          value={payoutStats ? formatNumberToDollar(payoutStats.lifetimePaid) : "—"}
          accent="#4caf50"
          detail="credited to wallet"
        />
        <StatCard
          label="AVG PAYOUT SPEED"
          value={payoutStats && payoutStats.avgPayoutDays != null ? `${payoutStats.avgPayoutDays}d` : "—"}
          accent="#af58f2"
          detail="received → paid"
        />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: isMobile ? "stretch" : "center", marginBottom: 20, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: isMobile ? "stretch" : "center", flexWrap: "wrap", width: isMobile ? "100%" : "auto", gap: isMobile ? 10 : 0 }}>
          <div style={{ display: "flex", alignItems: "center", flex: isMobile ? "1 1 0" : "0 0 auto", minWidth: 0, gap: isMobile ? 8 : 12 }}>
            {!isMobile && <RefreshButton onRefresh={handleRefresh} />}
            <div style={{ flex: isMobile ? 1 : "none", minWidth: 0 }}>
              <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} setDebouncedTerm={setDebouncedSearch} />
            </div>
          </div>

          <div style={{ marginLeft: isMobile ? 0 : 20, flexShrink: 0 }}>
            <DropdownMenu open={filterOpen} onOpenChange={handleFilterOpenChange}>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Filter size={13} style={{ marginRight: 6 }} />
                  Filter & Sort
                  {activeFilterCount > 0 && (
                    <span
                      style={{
                        marginLeft: 6,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 16,
                        height: 16,
                        borderRadius: 8,
                        background: "#00ADB5",
                        color: "#000",
                        fontSize: 10,
                        fontWeight: 800,
                        padding: "0 4px",
                      }}
                    >
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="tw-w-64">
                <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                {STATUS_OPTIONS.map((status) => (
                  <DropdownMenuCheckboxItem
                    key={status}
                    checked={selectedStatuses.includes(status)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(checked) => handleStatusChange(status, checked)}
                  >
                    {status.replace(/_/g, " ")}
                  </DropdownMenuCheckboxItem>
                ))}

                <DropdownMenuSeparator />

                <DropdownMenuLabel>Filter by Type</DropdownMenuLabel>
                {TYPE_OPTIONS.map((type) => (
                  <DropdownMenuCheckboxItem
                    key={type}
                    checked={selectedTypes.includes(type)}
                    onSelect={(e) => e.preventDefault()}
                    onCheckedChange={(checked) => handleTypeChange(type, checked)}
                  >
                    {type}
                  </DropdownMenuCheckboxItem>
                ))}

                <DropdownMenuSeparator />

                <DropdownMenuItem active={sortDirection === "ASC"} onClick={handleSortAscending}>
                  <ArrowUpNarrowWide size={14} style={{ marginRight: 8 }} />
                  Sort by Amount (Low to High)
                </DropdownMenuItem>
                <DropdownMenuItem active={sortDirection === "DESC"} onClick={handleSortDescending}>
                  <ArrowDownWideNarrow size={14} style={{ marginRight: 8 }} />
                  Sort by Amount (High to Low)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={filteredData}
        totalRows={totalRows}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={(newPage) => handleChangePage(newPage)}
        onRowsPerPageChange={(newValue) => handleChangeRowsPerPage(newValue)}
        loading={loadingChunk || isNavigatingToPage}
      />
    </div>
  );
};

export default MemberPayoutPage;
